import { IDLE_RPM, MAX_RPM } from '../sim/constants';

/**
 * Web Audio engine + effects.
 *
 * The engine is synthesised from a few detuned sawtooth oscillators whose base
 * frequency tracks propeller RPM (a C172 turns ~2400 rpm ≈ 40 Hz firing), giving
 * a convincing drone that rises and falls with power. Also provides a stall
 * warning horn and short beeps for marker/alert cues. Must be created/resumed
 * from a user gesture (browser autoplay policy).
 */
export class AudioEngine {
  private ctx: AudioContext;
  private master: GainNode;
  private engineGain: GainNode;
  private oscillators: OscillatorNode[] = [];
  private stallOsc: OscillatorNode | null = null;
  private stallGain: GainNode;
  engineEnabled = true;
  muted = false;
  private silenced = false; // ducked while paused, independent of the user's own mute
  private readonly masterVolume = 0.6;

  constructor() {
    this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.masterVolume;
    this.master.connect(this.ctx.destination);

    this.engineGain = this.ctx.createGain();
    this.engineGain.gain.value = 0;
    // Low-pass to soften the sawtooth into an engine drone.
    const lp = this.ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 800;
    this.engineGain.connect(lp);
    lp.connect(this.master);

    const detunes = [0, 7, -6, 12];
    for (const d of detunes) {
      const osc = this.ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = 40;
      osc.detune.value = d;
      osc.connect(this.engineGain);
      osc.start();
      this.oscillators.push(osc);
    }

    this.stallGain = this.ctx.createGain();
    this.stallGain.gain.value = 0;
    this.stallGain.connect(this.master);
  }

  resume(): void {
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  /** Master mute: silences everything (engine, horn, beeps), unlike
   * toggleEngineSound() below which only affects the engine drone. */
  toggleMute(): void {
    this.muted = !this.muted;
    this.applyMasterGain();
  }

  /** Duck all audio while paused, independent of (and without altering) the
   * user's own sound on/off preference — un-pausing restores whatever that
   * preference was. */
  setSilenced(active: boolean): void {
    if (this.silenced === active) return;
    this.silenced = active;
    this.applyMasterGain();
  }

  private applyMasterGain(): void {
    const target = this.silenced || this.muted ? 0 : this.masterVolume;
    this.master.gain.setTargetAtTime(target, this.ctx.currentTime, 0.05);
  }

  /** Update engine drone from current RPM (call each frame). */
  updateEngine(rpm: number): void {
    const now = this.ctx.currentTime;
    // Firing frequency: ~1 pulse per 1.5 revs for a 4-cyl 4-stroke; map to a
    // pleasant audible base that scales with RPM.
    const base = 22 + (rpm / MAX_RPM) * 55; // ~22..77 Hz fundamental
    for (const osc of this.oscillators) {
      osc.frequency.setTargetAtTime(base, now, 0.05);
    }
    const rpmFrac = Math.max(0, (rpm - IDLE_RPM) / (MAX_RPM - IDLE_RPM));
    const target = this.engineEnabled ? 0.06 + rpmFrac * 0.5 : 0;
    this.engineGain.gain.setTargetAtTime(target, now, 0.08);
  }

  toggleEngineSound(): void {
    this.engineEnabled = !this.engineEnabled;
  }

  /** Stall warning horn on/off (continuous tone while stalling). */
  setStallWarning(on: boolean): void {
    const now = this.ctx.currentTime;
    if (on && !this.stallOsc) {
      const osc = this.ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = 900;
      osc.connect(this.stallGain);
      osc.start();
      this.stallOsc = osc;
      this.stallGain.gain.setTargetAtTime(0.12, now, 0.02);
    } else if (!on && this.stallOsc) {
      this.stallGain.gain.setTargetAtTime(0, now, 0.05);
      const osc = this.stallOsc;
      this.stallOsc = null;
      osc.stop(now + 0.2);
    }
  }

  /** Short beep (e.g. marker/alert). freq Hz, duration seconds. */
  beep(freq = 1000, duration = 0.15, volume = 0.2): void {
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(volume, now + 0.01);
    g.gain.linearRampToValueAtTime(0, now + duration);
    osc.connect(g);
    g.connect(this.master);
    osc.start(now);
    osc.stop(now + duration + 0.02);
  }
}
